import { defineStore } from 'pinia';
import { demoConstructionCategories } from '@/Constants/demoData';
import {
    categoriesSections,
    elementDnDTemplate, typeToColorCategorySelected,
} from '@/Constants/common';
import { constructionItems } from '@/Constants/constructionItems';
import { isDecorInRoom, isDecorInSelectedRoom } from '@/lib/isDecorInSelectedRoom';
import { useConfigurationStore } from '@/Store/storeConfiguration';
import { isDecorHaveSurface } from '@/lib/isDecorHaveSurface';
import { configurationStoreDefaultState } from '@/Constants/configurationStoreDefaultState';
export const useStoreData = defineStore('storeData', {
    state: () => ({
        constructionCategories: [ ...demoConstructionCategories ],
        categories: [],
        activeCategoriesSection: categoriesSections.catalog,
        activeCategory: null,
        items: [],
        decors: [],
        roomTypes: [],
        constructionItems: [ ...constructionItems ],
        elementDnd: null,
        wallCategories: [],
        allWallCategories: [],
        floorCategories: [],
        allFloorCategories: [],
        categoriesItems: {},
        countItems: 0,
        zeroPrice: false,
        cost: 0,
        addBracketCovers: false,
    }),
    getters: {
        getCategoriesItems:(state)=>{
            return state.categoriesItems;
        },
        getAccessories: (state) => {
            const allItemIDs = useConfigurationStore().getAllItemIDs;
            const accessoriesIdsFromScene = {};
            allItemIDs.forEach((id) => {
                const category = state.categories.find(cat => {
                    if (cat.items && cat.items.includes(+id)) return true;
                    if (cat.sections) {
                        return cat.sections.some(section => section.items && section.items.includes(+id));
                    }
                    return false;
                });
                if (category && (category.id === 22 || category.id === 178)) {
                    accessoriesIdsFromScene[id] = (accessoriesIdsFromScene[id] || 0) + 1;
                }
            });

            const resultIds = { ...state.categoriesItems };
            Object.keys(accessoriesIdsFromScene).forEach(id => {
                resultIds[id] = (resultIds[id] || 0) + accessoriesIdsFromScene[id];
            });

            return [ ...useStoreData().getItemsByIdArr(
                Object.keys(resultIds)
                    .map(key => +key)).map(item => ({ ...item, count: resultIds[item.id] })) ];
        },
        getAccessoriesByCategoryId: (state) => {
            return (categoryId) => {
                // Находим категорию по ID
                const category = state.categories.find(cat => cat.id === categoryId);
                if (!category) return {};

                // Собираем все ID элементов из категории и ее подразделов
                const categoryItemIds = [];

                // Добавляем элементы из основной категории
                if (category.items) {
                    categoryItemIds.push(...category.items);
                }

                // Добавляем элементы из подразделов
                if (category.sections) {
                    category.sections.forEach(section => {
                        if (section.items) {
                            categoryItemIds.push(...section.items);
                        }
                    });
                }

                // Фильтруем categoriesItems только те, которые есть в категории
                const result = {};
                Object.keys(state.categoriesItems).forEach(key => {
                    const itemId = +key;
                    if (categoryItemIds.includes(itemId)) {
                        result[key] = state.categoriesItems[key];
                    }
                });

                // Собираем все ID элементов из 3D-сцены, принадлежащих этой категории
                const allItemIDs = useConfigurationStore().getAllItemIDs;
                const countsFromScene = {};
                allItemIDs.forEach(id => {
                    if (categoryItemIds.includes(+id)) {
                        countsFromScene[id] = (countsFromScene[id] || 0) + 1;
                    }
                });

                // Складываем ручной ввод и данные из сцены
                Object.keys(countsFromScene).forEach(id => {
                    result[id] = (result[id] || 0) + countsFromScene[id];
                });

                // Если категория 22 (Аксессуары), исключаем товары, которые уже есть в 178 (Новинки)
                if (categoryId === 22) {
                    const newItems = useStoreData().getAccessoriesByCategoryId(178);
                    Object.keys(newItems).forEach(id => {
                        delete result[id];
                    });
                }

                return result;
            };
        },
        getZeroPrice:(state)=>{
            return state.zeroPrice;
        },
        getAddBracketCovers:(state)=>{
            return state.addBracketCovers;
        },
        getCategories: (state) => {
            return state.categories;
        },
        getConstructionCategories: (state) => {
            return state.constructionCategories;
        },
        getActiveCategoriesSection: (state) => {
            return state.activeCategoriesSection;
        },
        getActiveCategory: (state) => {
            return state.activeCategory;
        },
        getRootCategoryData: (state) => {
            return (categoryID) => {
                if (!categoryID) {
                    return {};
                }
                return state.categories.filter(category => category.id === categoryID)[0];
            };
        },
        getSubcategories: () => {
            return (rootCategoryID) => {
                //TODO а почему useStoreData() вместо this?
                return useStoreData().getRootCategoryData(rootCategoryID).sections;
            };
        },
        getSubcategoriesOfActiveCategory: (state) => {
            return useStoreData().getSubcategories(state.activeCategory);
        },
        getActiveCategoryData: (state) => {
            return [ useStoreData().getRootCategoryData(state.activeCategory) ];
        },
        getAllItems: (state) => {
            return state.items;
        },
        getItemsByIdArr: (state) => {
            return (idArr) => {
                return state.items.filter(item => idArr.includes(item.id));
            };
        },

        // TODO refactoring
        getRootConstructionCategoryData: (state) => {
            return (categoryID) => {
                if (!categoryID) {
                    return {};
                }
                return state.constructionCategories.filter(category => category.id === categoryID)[0];
            };
        },
        getConstructionSubcategories: () => {
            return (rootCategoryID) => {
                return useStoreData().getRootConstructionCategoryData(rootCategoryID).sections;
            };
        },
        getSubcategoriesOfActiveConstructionCategory: (state) => {
            return useStoreData().getConstructionSubcategories(state.activeCategory);
        },
        getActiveConstructionCategoryData: (state) => {
            return [ useStoreData().getRootConstructionCategoryData(state.activeCategory) ];
        },
        getConstructionItemsByIdArr: (state) => {
            return (idArr) => {
                return state.constructionItems.filter(item => idArr.includes(item.id));
            };
        },

        getElementDnd: (state) => {
            // TODO
            return state.elementDnd;
        },
        getWallColorCategories: (state) => {
            return state.wallCategories;
        },
        getFloorColorCategories: (state) => {
            return state.floorCategories;
        },
        // getSelectedWallColorCategory: (state) => {
        //     return state.selectedWallCategory;
        // },
        // getSelectedFloorColorCategory: (state) => {
        //     return state.selectedFloorCategory;
        // },
        getWallColorOptions: (state) => {
            const category = state.wallCategories.filter(item =>
                item.id === useConfigurationStore().getRoomParamByName('selectedWallCategory'))[0];
            return state.decors.filter(item =>
                ([ ...category.items ].includes(item.id) && isDecorInSelectedRoom(item.rooms)));
        },
        getFloorColorOptions: (state) => {
            const category = state.floorCategories.filter(item =>
                item.id === useConfigurationStore().getRoomParamByName('selectedFloorCategory'))[0];
            return state.decors.filter(item =>
                ([ ...category.items ].includes(item.id) && isDecorInSelectedRoom(item.rooms)));
        },
        // getSelectedWallColor: (state) => {
        //     return state.selectedWallColor;
        // },
        // getSelectedFloorColor: (state) => {
        //     return state.selectedFloorColor;
        // },
        getRoomTypes: (state) => {
            return state.roomTypes;
        },
        getDecorById:(state) => {
            return (id) => {
                return state.decors.filter(item => item.id === id)[0];
            };
        },
        getCost: (state) => {
            return state.cost;
        },
        getModelledDecorsByRoomCode:(state) => {
            return (roomCode) => {
                return state.decors.filter((item) => {
                    ![ '', null, undefined ].includes(item.url) && isDecorInRoom(item.rooms, roomCode);
                });
            };
        },
        getModelledDecorsOfSelectedRoom:(state) => {
            return state.decors.filter((item) =>
                ![ '', null, undefined ].includes(item.url) && isDecorInSelectedRoom(item.rooms),
            );
        },
        getModelledDecorsOfSelectedRoomBySurfaceCode:(state) => {
            return state.decors.filter((item) =>
                ![ '', null, undefined ].includes(item.url) &&
                isDecorInSelectedRoom(item.rooms) &&
                isDecorHaveSurface(item.surfaces),
            );
        },
    },
    actions: {
        receiveCatalog(data) {
            this.items = [ ...data.items ];
            this.decors = [ ...data.decors ];
            let roomTypesLib = {};
            const roomSet = new Set();
            const rooms = [];
            for (let i in data.decors) {
                for (let ii in data.decors[i].rooms) {
                    if (roomSet.has(data.decors[i].rooms[ii].code)) continue;
                    roomSet.add(data.decors[i].rooms[ii].code);
                    rooms.push(data.decors[i].rooms[ii]);
                    // roomTypesLib[data.decors[i].rooms[ii].code] = data.decors[i].rooms[ii].value;
                }
            }
            rooms.sort((room1, room2)=>room1.sort-room2.sort);
            rooms.forEach((room)=>{
                roomTypesLib[room.code] = room.value;
            });
            this.roomTypes = roomTypesLib;
            configurationStoreDefaultState.room.selectedRoomType = Object.keys(roomTypesLib)[0];
            useConfigurationStore().setRoomParamByName('selectedRoomType', Object.keys(roomTypesLib)[0]);

            this.categories = [ ...data.sections[0].sections ];
            const wallCategory = data.decorSections.filter(item => item.code === 'walls')[0];
            const floorCategory = data.decorSections.filter(item => item.code === 'floor')[0];
            if (wallCategory) {
                this.allWallCategories = [ ...wallCategory.sections ];
                configurationStoreDefaultState.room.selectedWallCategory = wallCategory.sections[0].id;
                useConfigurationStore().setRoomParamByName('selectedWallCategory', wallCategory.sections[0].id);
            }
            if (floorCategory) {
                this.allFloorCategories = [ ...floorCategory.sections ];
                configurationStoreDefaultState.room.selectedFloorCategory = floorCategory.sections[0].id;
                useConfigurationStore().setRoomParamByName('selectedFloorCategory', floorCategory.sections[0].id);
            }
            //Сортировка декора под тип комнаты
            this.updateRoomDecor();
        },
        updateRoomDecor() {
            // useConfigurationStore().setRoomParamByName('selectedRoomType','wardrobe');
            let roomType = useConfigurationStore().room.selectedRoomType;
            //Получение всего декора, доступного для этой комнаты
            let roomDecors = this.decors.filter((decor) => {
                if (decor.rooms) {
                    return decor.rooms.some((decorRoom) => decorRoom.code === roomType);
                }
            });
            //Ограничение категорий
            this.wallCategories = JSON.parse(JSON.stringify(this.allWallCategories.filter((category) => category.items.some((item) => roomDecors.some((decor) => decor.id === item)))));
            this.floorCategories = JSON.parse(JSON.stringify(this.allFloorCategories.filter((category) => category.items.some((item) => roomDecors.some((decor) => decor.id === item)))));
            //Установка цвета и пола в зависимости от выбранного типа
            //получить отсортированные массивы декора для пола и стен и их первые члены назначить цветом
            let wallItems = roomDecors.filter((decor)=>
                this.wallCategories.map((cat)=>cat.items)
                .flat()
                .includes(decor.id));
            wallItems.sort((item1,item2)=>item1.sort - item2.sort);
            let floorItems = roomDecors.filter((decor)=>
                this.floorCategories.map((cat)=>cat.items)
                    .flat()
                    .includes(decor.id));
            floorItems.sort((item1,item2)=>item1.sort - item2.sort);
            const wallId = wallItems[0].id;
            const floorId = floorItems[0].id;
            const wallCat = this.wallCategories.find((cat)=>cat.items.includes(wallId));
            const floorCat = this.floorCategories.find((cat)=>cat.items.includes(floorId));
            if (wallCat) {
                configurationStoreDefaultState.room.selectedWallCategory = +wallCat.id;
                useConfigurationStore().setColorCategory('wall', wallCat.id);
            }
            if (floorCat) {
                configurationStoreDefaultState.room.selectedFloorCategory = +floorCat.id;
                useConfigurationStore().setColorCategory('floor', floorCat.id);
            }
            configurationStoreDefaultState.room.selectedWallColor = wallId;
            configurationStoreDefaultState.room.selectedFloorColor = floorId;
            useConfigurationStore().setColor('wall', wallId);
            useConfigurationStore().setColor('floor', floorId);
        },

        setZeroPrice(bool) {
            this.zeroPrice = bool;
        },
        setAddBracketCovers(flag) {
            this.addBracketCovers = !!flag;
        },
        setCategoriesItems(id, value) {
             let count = this.categoriesItems[+id] ?? 0;
             count += value;
             if (count < 0) return;
             this.categoriesItems[+id] = count;
        },
        setActiveCategory(sectionId, categoryId) {
            this.activeCategoriesSection = sectionId;

            if (categoryId === this.activeCategory) {
                this.activeCategory = null;
            } else {
                this.activeCategory = categoryId;
            }
        },
        setDnDElement(data, x, y) {
            let el = elementDnDTemplate;
            let category = this.categories.filter(category =>
                category.id === useStoreData().getActiveCategory)[0]??{};
            el.data = JSON.parse(JSON.stringify(data));
            el.category = JSON.parse(JSON.stringify(category));
            el.startX = x;
            el.startY = y;
            this.elementDnd = el;
        },
        setDnDElementVisible(flag) {
            let el = JSON.parse(JSON.stringify(this.elementDnd));
            if (!el) return;
            el.visible = flag;
            this.elementDnd = el;
        },
        resetDnDElement() {
            // TODO Drop
            this.elementDnd = null;
        },
        removeItemFromCart(id){
            delete this.categoriesItems[id];
        },
        // setColorCategory(type, colorId) {
        //     this[typeToColorCategorySelected[type]] = +colorId;
        // },
        // setColor(type, colorId) {
        //     this[typeToColorSelected[type]] = +colorId;
        // },
        setCost(value) {
            this.cost = value;
        },
        setAccessories(data) {
            if (!data) return;
            this.categoriesItems = {};
            const allItemIDs = useConfigurationStore().getAllItemIDs;
            const sceneCounts = {};
            allItemIDs.forEach(id => {
                sceneCounts[id] = (sceneCounts[id] || 0) + 1;
            });

            data.forEach((category) => {
                if (!category.id || !category.count) return;
                // При загрузке аксессуаров мы вычитаем те, что уже есть в 3D-сцене,
                // потому что они будут добавлены автоматически геттером getAccessoriesByCategoryId.
                // Это предотвращает экспоненциальный рост при циклах сохранение-загрузка.
                const manualCount = Math.max(0, category.count - (sceneCounts[category.id] || 0));
                if (manualCount > 0) {
                    this.categoriesItems[category.id] = manualCount;
                }
            });
        },
    },
});
